// TODO: Split this file into Search.js and Sort.js
/* eslint-disable max-classes-per-file */

import {
    YEAR_MS,
    DAY_MS,
    FEATURED_SORT_DAY_FALLOFF,
    FEATURED_SORT_INSTALLS_WEIGHT,
    FEATURED_SORT_INSTALLS_WEIGHT_CEIL,
} from "../common/constants";
import Utils from "./utils";

export default class Search {
    // This function serves as a redirection method, it detects the type that is being passed in and sends it to the appropriate function
    // entry, the value to be checked
    // value, the value to check entry against
    static matches(entry, value, options = {}) {
        if (entry === undefined) {
            entry = "";
        }

        if (entry instanceof Array) {
            return Search.ArrayMatches(entry, value, options);
        }
        const type = typeof entry;
        switch (type) {
            case "string":
                return Search.StringMatches(entry, value, options);
            case "boolean":
                return Search.BooleanMatches(entry, value, options);
            case "number":
                return Search.NumberMatches(entry, value, options);
            case "object":
                return Search.ObjectMatches(entry, value, options);
            default:
                return false;
        }
    }
    static BooleanMatches(entry, value, options = {}) {
        if (typeof entry !== "boolean") {
            return false;
        }
        if (value === undefined) {
            return !!options.exclude;
        }

        return entry === value;
    }
    static StringMatches(entry, value, options = {}) {
        if (typeof entry !== "string") {
            return false;
        }
        if (value === undefined) {
            return !!options.exclude;
        }

        if (value instanceof Array) {
            // If the value being passed in is an array, then switch it around and find the string in the array
            return Search.ArrayMatches(value, entry, options);
        }
        let entryToCheck = entry.toLowerCase();
        let valueToCheck = value.toString().toLowerCase();
        // Check if this string is wrapped in quotes and remove them
        if (valueToCheck.startsWith('"') && valueToCheck.endsWith('"')) {
            valueToCheck = valueToCheck.substring(1, valueToCheck.length - 1);
        }
        // Slugify the string if the search requires it
        if (options.slugify) {
            entryToCheck = Utils.slugify(entryToCheck || "");
            valueToCheck = Utils.slugify(valueToCheck || "");
        }

        let hasString = false;
        if (options.startswith && !options.endswith) {
            hasString = entryToCheck.startsWith(valueToCheck);
        } else if (options.endswith && !options.startswith) {
            hasString = entryToCheck.endsWith(valueToCheck);
        } else {
            const strict = options.startswith && options.endswith ? false : options.strict;
            hasString = strict ? entryToCheck === valueToCheck : entryToCheck.includes(valueToCheck);
        }

        return options.exclude ? !hasString : hasString;
    }
    // The number filter can't accept any of the options that a string or array search can because it's based on numbers
    // rather than string manipulaton
    static NumberMatches(entry, value, options = {}) {
        if (typeof entry !== "number") {
            return false;
        }
        if (value === undefined) {
            return false;
        }

        if (value instanceof Array) {
            // If the value being passed in is an array, then switch it around and find the string in the array
            return Search.ArrayMatches(value, entry, options);
        }
        if (typeof value === "number") {
            return entry === value;
        }
        if (typeof value === "string") {
            const val = value.trim();
            if (val.startsWith("==")) {
                return entry === Number(val.slice(2));
            }
            if (val.startsWith("=")) {
                return entry === Number(val.slice(1));
            }
            if (val.startsWith(">=")) {
                return entry >= Number(val.slice(2));
            }
            if (val.startsWith(">")) {
                return entry > Number(val.slice(1));
            }
            if (val.startsWith("<=")) {
                return entry <= Number(val.slice(2));
            }
            if (val.startsWith("<")) {
                return entry < Number(val.slice(1));
            }
        }
        return entry == value;
    }
    static ArrayMatches(entry, value, options = {}) {
        if (!(entry instanceof Array)) {
            return false;
        }

        if (entry.length === 0) {
            return !!options.exclude;
        }

        const fn = options.exclude ? entry.every : entry.some;
        return fn.call(entry, (entry) => Search.matches(entry, value, options));
    }

    // options.key contains the key to the object property to use
    // __key searches all keys an Object
    // __value searches all values of an Object
    static ObjectMatches(entry, value, options = {}) {
        if (options.key && options.key !== "__key" && options.key !== "__value") {
            const parts = options.key.split(".");
            const key = parts.shift();
            if (parts.length === 0) {
                delete options.key;
            } else {
                options.key = parts.join(".");
            }
            return Search.matches(entry[key], value, options);
        }
        let entries = [];
        if (!options.key || options.key === "__key") {
            entries = entries.concat(Object.keys(entry || {}));
        }
        if (!options.key || options.key === "__value") {
            entries = entries.concat(Object.values(entry || {}));
        }
        return Search.matches(entries, value, options);
    }

    // This is used by the vue components to have a consistent string split
    static splitString(filter, delim = "\\s") {
        return filter.match(new RegExp(`(?:[^${delim}"]+|"[^"]*")+`, "g")) || []; //.filter((f) => !!f);
    }

    async init(packages) {
        // Make array of objects have unique entries based on key field, then sort them by the name field
        function extractUnique(array) {
            return Object.values(
                array.reduce((obj, item) => {
                    obj[item.key] = item;
                    return obj;
                }, {})
            ).sort((a, b) => a.name.localeCompare(b.name));
        }
        if (packages) {
            this.systems = packages
                .filter((p) => p.type === "system") // Get the packages that are systems
                .map((p) => ({ key: p.id, name: p.title })) // extract the data we need
                .sort((a, b) => a.name.localeCompare(b.name)); // Sort the systems
            this.authors = extractUnique(
                packages.flatMap((p) => p.authors.map((a) => ({ key: Utils.slugify(a), name: a })))
            ); // Get all the authors in all the packages
            this.languages = extractUnique(
                packages.flatMap((p) => p.languages.map((p) => ({ key: p.lang, name: p.name }))) // Get all the languages in all the packages
            );
            this.categories = extractUnique(
                packages
                    .filter((p) => p.type === "module") // Get the packages that are modules, since categories only apply to that type
                    .flatMap((p) =>
                        Object.entries(p.tags).map(([k, v]) => ({
                            key: k,
                            name: v,
                            parent: "category",
                            selectable: true,
                            disabled: false,
                            visible: true,
                            sort: 0,
                        }))
                    )
            );
            this.creators = extractUnique(
                packages
                    .filter((p) => !!p.creator && p.creator.vanity && p.creator.name) // Get a list of creators
                    .map((p) => ({ key: p.creator.vanity, name: p.creator.name }))
            );
        }

        // Get all bazaar tags from browser cache
        const bazaarTags = localStorage.getItem("bazaarTags");
        if (bazaarTags) {
            // If there's a value there then try and parse it
            try {
                this.bazaarTags = Search.mapTagData(JSON.parse(bazaarTags));
            } catch (e) {
                this.bazaarTags = [];
            }
        } else {
            this.bazaarTags = [];
        }

        if (!this.bazaarTags.length) {
            // We don't have the data yet, so wait for it
            await this.getTagData();
        } else {
            // We have the data, but we want to update it in the background
            this.getTagData();
        }
    }

    async getTagData() {
        // Get the bazaar tags from the server
        const result = await Utils.api("bazaar/tags");
        if (result.success) {
            // Store the tags in the browser cache
            try {
                localStorage.setItem("bazaarTags", JSON.stringify(result.tags));
                this.bazaarTags = Search.mapTagData(result.tags);
            } catch (e) {
                this.bazaarTags = [];
            }
        } else {
            this.bazaarTags = [];
        }
    }

    // This function maps the data from the server into a format that the search component can use
    static mapTagData(data) {
        const findTagChildren = (tagList, parent, depth = 0) => {
            if (depth > 10) {
                return [];
            }
            const children = tagList
                .filter((bt) => bt.parent === parent)
                .flatMap((bt) => [bt.tag, ...findTagChildren(tagList, bt.tag, depth + 1)]);
            return children;
        };

        const bazaarTags = data.map((bt) => {
            return {
                key: bt.tag,
                name: t(`BAZAAR_TAG.${bt.tag}`, { defaultValue: bt.label || bt.tag }),
                _name: bt.label,
                sort: bt.displayOrder,
                visible: bt.visible,
                disabled: bt.disabled,
                parent: bt.parent,
                selectable: bt.selectable,
            };
        });
        for (const bt of bazaarTags) {
            bt.children = findTagChildren(bazaarTags, bt.key);
        }

        return bazaarTags;
    }

    constructor() {
        this.bazaarTags = [];
        this.systems = [];
        this.authors = [];
        this.languages = [];
        this.categories = [];
        this.creators = [];

        /*
            tagkey: {
            name: string, // Name of the tag
            description: string, // Description of the tag
            example: string, // Example of how to use the tag, optional
            icon: string, // Icon to use for the tag, optional
            parse: true/false, // Try to build a hierarchy of tags, optional, default is false
            selectable: true/false, // Allow this tag to be selected, optional, default is true
            placeholder: string, // Placeholder text to show in the dropdown type field, optional
            sort: number, // Used to sort the tags, optional, default is 0
            strict: true/false, // any value entered *must* exist in the options list, optional, default is false
            searchWeight: number, // How much extra weight to give this tag when searching, optional, default is 1
            open: true/false, // This suggestion will always show as open, optional, default is false
            include: Array, // [{key: "what", value: "foundry"}], // Include these tags when this tag is added, optional
            limit: Object, // { key:"foundry" } or { key: "what", value: "foundry" }, Limit this tag to only be added if these tags are present, optional
            includeAll: true, // All options in this tag must be true for the pkg to be included
            visible: true/false or { default: true, bazaarFilter: false }, // Boolean or Object to specify what visibility key it will show for.  default is used for if it's opt in or opt out of visibility
            allowAutocomplete: true/false, // Allow this tag to show autocompelete suggestions in the search box
            type: Array, // Available options "dropdown", "minmax", "checkboxlist"(default) to determine how the tag is displayed in the filter list
            options: () => { return [] },
                option = { key: "art", name: "Art", disabled: true, visible: false, sort: 0, tristate: true, useKey: otherKey, type: [] }
            restrict: (tags, tag) => {}, // Function to test if tags should be removed with the addition of the new tag
            filter: (pkg, value) => {} // Function to test if the pkg should be included
            },
        */
        this._searchTags = {
            is: {
                name: "",
                description: t(
                    "What type of thing are you looking for? Foundry related, Forge content, D&D Beyond material?"
                ),
                strict: true,
                open: true,
                visible: { creatorSearch: false },
                options: () => [
                    { tag: "foundry", sort: 0 },
                    { tag: "forge", sort: 1 },
                    { tag: "dndbeyond", sort: 2 },
                    { tag: "creator", visible: false, sort: 3 },
                    { tag: "art", visible: false, sort: 4 },
                    { tag: "book", visible: false, sort: 4 },
                ],
                filter: (pkg, value, options = {}) => {
                    if (value === "foundry") {
                        return Search.matches(pkg.type, ["system", "module", "world"], options);
                    } else if (value === "forge") {
                        return Search.matches(pkg.type, ["forge-gift-card", "asset"], options);
                    }
                    return Search.matches(pkg.type, value, options);
                },
            },
            foundry: {
                name: t("Foundry VTT"),
                description: t("Search for specific Foundry VTT content types (Modules, Systems, or Worlds)"),
                strict: true,
                icon: "/images/fvtt-d20-32x32.png",
                visible: { creatorSearch: false },
                options: () => [
                    { key: "system", name: t("Game Systems"), sort: 1, icon: "/images/bazaar/system-icon-32.png" },
                    {
                        key: "module",
                        name: t("Add-on Modules"),
                        sort: 1,
                        type: ["menu"],
                        icon: "/images/bazaar/module-icon-32.png",
                    },
                    { key: "world", name: t("Worlds"), sort: 1, icon: "/images/bazaar/world-icon-32.png" },
                ],
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.type, value, options);
                },
            },
            forge: {
                name: t("The Forge"),
                description: t("Search through content exclusively on The Forge or Forge Gift Cards"),
                strict: true,
                icon: "/images/the-forge-logo-32x32.png",
                visible: { creatorSearch: false },
                options: () => [
                    { key: "asset", name: t("Asset Packs"), sort: 1, icon: "images/bazaar/asset-icon-32.png" },
                    { key: "forge-gift-card", name: t("Gift Cards"), sort: 1 },
                ],
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.type, value, options);
                },
            },
            dndbeyond: {
                name: t("D&D Beyond"),
                description: t("Search for D&D beyond content that has been integrated with The Forge"),
                strict: true,
                icon: "/images/dnd-beyond.png",
                visible: { creatorSearch: false },
                options: () => [
                    { key: "adventure", name: t("Adventures"), sort: 1 },
                    { key: "rule", name: t("Rules"), sort: 1 },
                ],
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.category, value, options);
                },
            },
            assets: {
                parent: { key: "asset" },
                open: true,
                parse: true,
                selectable: false,
                visible: { default: false, bazaarFilter: true },
                type: ["menu", "checklist"],
                filter: () => {
                    // This is just a placeholder for tags
                },
            },
            tag: {
                name: t("Tags"),
                description: t("Additional search tags"),
                example: "tag:battlemaps",
                type: ["menu", "checklist"],
                parse: true,
                open: true,
                options: () => {
                    return (
                        [
                            ...this.bazaarTags,
                            {
                                key: "category",
                                name: t("Category"),
                                disabled: false,
                                visible: true,
                                selectable: false,
                                parent: null,
                                sort: 9999,
                                parse: false,
                            },
                            ...this.categories,
                        ] || []
                    );
                },
                filter: (pkg, value, options = {}) => {
                    const valueLowerCase = value.toLowerCase();
                    const tag = this.bazaarTags.find((bt) => {
                        return (
                            bt.name?.toLowerCase() === valueLowerCase ||
                            bt._name?.toLowerCase() === valueLowerCase ||
                            bt.key?.toLowerCase() === valueLowerCase
                        );
                    });

                    const hasTags = tag
                        ? Search.matches(
                              pkg.bazaarTags,
                              [tag.key, ...tag.children],
                              Object.assign(options, { strict: true })
                          )
                        : !!options.exclude;
                    const hasTagsLabel = Search.matches(
                        pkg.bazaarTags,
                        value,
                        Object.assign({ strict: true }, options)
                    );
                    // __key searches all keys, and __value searches all values of an Object
                    const hasCategory = Search.matches(pkg.tags, value, Object.assign(options, { strict: true }));

                    // The hasTags, hasTagsLabel and hasCategory variables are already 'negated' from the Search.matches
                    // result, so we need to AND them when exclude is true, without negating them again.
                    return options.exclude
                        ? hasTags && hasTagsLabel && hasCategory
                        : hasTags || hasTagsLabel || hasCategory;
                },
            },
            system: {
                name: t("System"),
                description: t("Content compatible with this system"),
                example: "system:dnd5e",
                type: ["dropdown"],
                parse: false,
                placeholder: t("Choose a System"),
                icon: "/images/bazaar/system-icon-32.png",
                options: () => this.systems || [],
                filter: (pkg, value, options = {}) => {
                    // Collect additional keys if the user entered in the name of the system
                    let values = value;
                    const systemOptions = this._searchTags.system.options;
                    if (systemOptions instanceof Array) {
                        values = systemOptions
                            .filter((o) => Search.matches(o.name, value, options))
                            .map((o) => o.key)
                            .push(value);
                    }
                    if (pkg.type === "system") {
                        return Search.matches(pkg.id, values, options);
                    }
                    return Search.matches(pkg.systems, values, options);
                },
            },
            author: {
                name: t("Author"),
                description: t("Author of the package"),
                example: "author:KaKaRoTo",
                type: ["dropdown"],
                parse: false,
                visible: { bazaarFilter: false, creatorSearch: false },
                options: () => this.authors || [],
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.authors, value, Object.assign({ slugify: true }, options));
                },
            },
            language: {
                name: t("Language"),
                description: t("Language the package supports"),
                example: "language:fr",
                type: ["dropdown"],
                icon: "/images/internationalization.png",
                parse: false,
                placeholder: t("Choose a Language"),
                options: () => this.languages || [],
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.languages, value, Object.assign({ key: "lang" }, options));
                },
            },
            creator: {
                name: t("Creator"),
                description: t("Packages presented by this creator"),
                example: "creator:The Forge",
                type: ["dropdown"],
                parse: false,
                visible: { bazaarFilter: false, creatorSearch: false },
                options: () => this.creators || [],
                filter: (pkg, value, options = {}) => {
                    if (pkg.type === "creator") {
                        return Search.matches(pkg.name, value, options);
                    }
                    if (pkg.creator) {
                        const hasName = Search.matches(pkg.creator?.name, value, options);
                        const hasVanity = Search.matches(pkg.creator?.vanity, value, options);
                        // The hasName and hasVanity variables are already 'negated' from the Search.matches
                        // result, so we need to AND them when exclude is true, without negating them again.
                        return options.exclude ? hasName && hasVanity : hasName || hasVanity;
                    }
                    return !!options.exclude;
                },
            },
            price: {
                name: t("Price"),
                description: t("Search by the price of a package"),
                example: "price:<20",
                includeAll: true,
                type: ["checklist", "minmax"],
                options: () => [
                    { key: "free", name: t("Free"), reverseName: t("Not free"), tristate: true, sort: 0 },
                    {
                        key: "pwyw",
                        name: t("Pay what you can"),
                        reverseName: t("Fixed price"),
                        tristate: true,
                        sort: 1,
                    },
                    { key: "on-sale", name: t("On Sale"), sort: 2 },
                    { key: "patreon", name: t("Patreon Integrated"), sort: 3 },
                    { key: "premium", name: t("Premium packages"), tristate: true, sort: 4 },
                    { key: "marketplace", name: t("Bazaar Marketplace"), tristate: true, sort: 5 },
                ],
                restrict: (values = [], tag) => {
                    // If it starts with a number, then prepend the equals so we can calculate rules properly
                    if (Number.isInteger(parseInt(tag[0]))) {
                        tag = `=${tag}`;
                    }
                    // if the tag is = then remove all other price tags
                    if (tag.startsWith("=") || tag === "free" || tag === "pwyw") {
                        values = [];
                    }
                    // if the tag starts with > or < then remove any tags other than the opposite operator
                    else if (tag.startsWith(">")) {
                        values = values.filter((t) => !(t.trim().startsWith(">") || t === "free"));
                    } else if (tag.startsWith("<")) {
                        values = values.filter((t) => !(t.trim().startsWith("<") || t === "free"));
                    }
                    values.push(tag);

                    return values;
                },
                filter: (pkg, value, options = {}) => {
                    if (value.endsWith("free")) {
                        return options.exclude ? !!pkg.price : !pkg.price && !pkg.premium;
                    } else if (value.endsWith("pwyw")) {
                        return options.exclude ? !pkg.pwyw : !!pkg.pwyw;
                    } else if (value.endsWith("on-sale")) {
                        const onsale = pkg.price && pkg.preSalePrice && pkg.preSalePrice !== pkg.price;
                        return options.exclude ? !onsale : !!onsale;
                    } else if (value.endsWith("patreon")) {
                        return options.exclude ? !pkg.patreon : !!pkg.patreon;
                    } else if (value.endsWith("premium")) {
                        if (pkg.premium === "dndbeyond") {
                            return false;
                        }
                        return options.exclude ? !pkg.premium : !!pkg.premium;
                    } else if (value.endsWith("marketplace")) {
                        const marketplace = pkg.premium && pkg.premium === "marketplace";
                        return options.exclude ? !marketplace : marketplace;
                    }
                    const price = pkg.pwywMinimum ? pkg.pwywMinimum : pkg.price;
                    return Search.matches(price, value, options);
                },
            },
            rating: {
                name: t("Rating"),
                description: t("Search by the rating of a package"),
                example: "rating:>4",
                includeAll: true,
                visible: false, //This is hidden until ratings are implemented
                type: ["minmax"],
                restrict: (values = [], tag) => {
                    // If the tag starts with > or < then remove any tags other than the opposite operator
                    if (tag.startsWith(">")) {
                        values = values.filter((t) => !t.trim().startsWith(">"));
                    } else if (tag.startsWith("<")) {
                        values = values.filter((t) => !t.trim().startsWith("<"));
                    }
                    values.push(tag);

                    return values;
                },
                filter: (pkg, value, options = {}) => {
                    const rating = pkg.rating ? pkg.rating : 0;
                    return Search.matches(rating, value, options);
                },
            },
            name: {
                name: t("Name"),
                description: t("Name of item"),
                example: "name:1001 Fish",
                visible: { bazaarFilter: false },
                allowAutocomplete: true,
                searchWeight: 5, // Name should have more weight than other fields combined
                filter: (pkg, value, options = {}) => {
                    if (pkg.type === "creator") {
                        return Search.matches(pkg.name, value, options);
                    }
                    return Search.matches(pkg.title, value, options);
                },
            },
            id: {
                name: t("ID"),
                description: t("ID of item"),
                example: "id:dice-so-nice",
                visible: { bazaarFilter: false },
                allowAutocomplete: true,
                searchWeight: 3,
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.id, value, Object.assign({}, options, { strict: true }));
                },
            },
            has: {
                name: t("Quick Filters"),
                description: t("Status of package"),
                example: "has:updates",
                open: true,
                includeAll: true,
                options: () => [
                    { key: "marketplace", name: t("Bazaar Marketplace"), tristate: true, useKey: "price", sort: 1 },
                    { key: "on-sale", name: t("On Sale"), useKey: "price", sort: 2 },
                    {
                        key: "installed",
                        name: t("Installed"),
                        reverseName: t("Not Installed"),
                        tristate: true,
                        sort: 3,
                    },
                    { key: "updates", name: t("Has Update"), reverseName: t("Has No Update"), tristate: true, sort: 4 },
                    { key: "custom", name: t("Custom"), tristate: true, sort: 5 },
                    { key: "favorite", name: t("Favorite"), visible: false, tristate: true, sort: 6 }, // This is hidden until favorites are implemented
                ],
                filter: (pkg, value, options = {}) => {
                    if (value.endsWith("updates")) {
                        const update = pkg.installed && pkg.latest && pkg.installed !== pkg.latest;
                        return options.exclude ? !update : update;
                    } else if (value.endsWith("installed")) {
                        return options.exclude ? !pkg.installed : pkg.installed;
                    } else if (value.endsWith("custom")) {
                        return options.exclude ? !pkg.custom : pkg.custom;
                    } else if (value.endsWith("favorite")) {
                        return options.exclude ? !pkg.favorite : pkg.favorite;
                    }
                },
            },
            // This shouldn't ever be used it's just to record the search weight of the description
            description: {
                name: t("Description"),
                description: t("Description of the package"),
                visible: false, // { bazaarFilter: false }
                filter: (pkg, value, options = {}) => {
                    return Search.matches(pkg.description, value, options);
                },
            },
            // Term is used by the search component to keep track of text typed into the search field
            // It should never be a visible option as the search component redirects it to the search input field
            terms: {
                visible: false,
                filter: (pkg, value, options = {}) => {
                    // If this is a package, then check the title, author, and description
                    // Do non-strict check on id when it's coming from the free-form terms
                    const has = {};
                    has.id = this.searchTags.id.filter(pkg, value, {
                        ...options,
                        startswith: true,
                        endswith: true,
                    });
                    has.name = this.searchTags.name.filter(pkg, value, options);
                    has.description = this.searchTags.description.filter(pkg, value, options);
                    if (pkg.type === "creator") {
                        if (options.exclude) {
                            return has.id && has.name && has.description;
                        }
                        return Object.keys(has).reduce((partialSum, key) => {
                            let weight = 0;
                            if (has[key]) {
                                weight = this.searchTags[key].searchWeight ? this.searchTags[key].searchWeight : 1;
                            }
                            return partialSum + weight;
                        }, 0);
                    }

                    has.author = this.searchTags.author.filter(pkg, value, options);
                    has.tag = this.searchTags.tag.filter(pkg, value, { ...options, strict: false });
                    // The has variables are already 'negated' from the Search.matches and tag.filter
                    // result, so we need to AND them when exclude is true, without negating them again.
                    if (options.exclude) {
                        return has.id && has.author && has.name && has.description && has.tag;
                    }
                    return Object.keys(has).reduce((partialSum, key) => {
                        let weight = 0;
                        if (has[key]) {
                            weight = this.searchTags[key].searchWeight ? this.searchTags[key].searchWeight : 1;
                        }
                        return partialSum + weight;
                    }, 0);
                },
            },
        };
    }

    get searchTags() {
        return this._searchTags;
    }

    search(packages, filterTags, options = { remove: true }) {
        // clear out any filters that aren't necessary
        const useFilterTags = JSON.parse(JSON.stringify(filterTags));
        for (let [key, value] of Object.entries(useFilterTags)) {
            if (value) {
                const tag = (options.searchTags && options.searchTags[key]) || this.searchTags[key];
                if (tag?.strict) {
                    value = value.filter((v) => tag.options().find((o) => o.key === v || o.tag === v));
                }
                if (value.length === 0) {
                    delete useFilterTags[key];
                }
            } else {
                delete useFilterTags[key];
            }
        }
        for (const pkg of packages) {
            const result = {
                visible: true,
                matches: 0,
                weight: 0,
                marketplace: pkg.premium === "marketplace",
                daysSincePublished: Math.max(
                    Math.floor((new Date() - new Date(pkg.firstPublishAt || pkg.created)) / DAY_MS),
                    0
                ),
                installs: Math.floor(pkg.installs * 100) || 0,
            };

            for (let [k, values] of Object.entries(useFilterTags)) {
                const tag = (options.searchTags && options.searchTags[k]) || this.searchTags[k];
                if (!tag) {
                    continue;
                }
                // 'Or' all the values, 'And' all the tags
                let totalOr = 0;
                let countOr = 0;
                for (const value of values) {
                    const require = value.startsWith("+");
                    const exclude = value.startsWith("-");
                    let filterValue = require || exclude ? value.substring(1) : value;
                    const strict = value.startsWith("=");
                    filterValue = strict ? value.substring(1) : value;
                    const endswith = filterValue.startsWith("*");
                    filterValue = endswith ? filterValue.substring(1) : filterValue;
                    const startswith = filterValue.endsWith("*");
                    filterValue = startswith ? filterValue.slice(0, -1) : filterValue;

                    if (
                        tag.strict &&
                        !(tag.options && tag.options().find((o) => o.key === filterValue || o.tag === filterValue))
                    ) {
                        // Do not process this value if this tag is strict and that option doesn't exist
                        continue;
                    }

                    // Remove the containing quotes
                    if (filterValue.startsWith('"') && filterValue.endsWith('"')) {
                        filterValue = filterValue.substring(1, filterValue.length - 1);
                    }

                    let filterWeight = 0;
                    if (tag.filter) {
                        filterWeight = tag.filter(pkg, filterValue, { require, exclude, strict, endswith, startswith });
                        if (filterWeight instanceof Boolean) {
                            filterWeight = filterWeight ? (tag.searchWeight === undefined ? 1 : tag.searchWeight) : 0;
                        }
                    }

                    if (filterWeight) {
                        result.matches += 1;
                        result.weight += filterWeight;
                    }

                    if (require || exclude || tag.includeAll) {
                        if (!filterWeight) {
                            // One of the required searches failed, no need to continue searching
                            result.matches = 0;
                            break;
                        }
                    } else {
                        totalOr++;
                        if (filterWeight) {
                            countOr++;
                        }
                    }
                }

                // If there are any And searches then there needs to be at least one Or
                result.visible = result.visible && result.matches > 0 && (totalOr === 0 || countOr > 0);
                result.allmatch = result.matches >= values.length;

                // If the pkg has been eliminated, then there's no reason to continue comparing search fields.
                if (!result.visible) {
                    break;
                }
            }
            pkg.search = result;
        }

        return options.remove ? packages.filter((p) => p.search.visible) : packages;
    }
}

const YEAR_AGO_MS = new Date().getTime() - YEAR_MS;
const DEFAULT_SORT = "featured";

export class Sort {
    static get defaultSort() {
        return DEFAULT_SORT;
    }
    static get sortTags() {
        return {
            featured: {
                name: t("Featured"),
                icon: "fas fa-cubes",
                sort: (packages) => {
                    const sortedPackages = packages.sort(Sort.compareByWeight);
                    // This could likely be optimized, but it's somewhat unavoidable to iterate the array
                    // multiple times. We must calculate the featured weight before sorting by it, and we need
                    // to sort by featured weight once before demoting same-creator packages.
                    for (const pkg of sortedPackages) {
                        pkg.search = pkg.search || {};
                        pkg.search.featuredWeight = Sort.calculateFeaturedWeight(pkg.search);
                    }
                    sortedPackages.sort(Sort.compareByFeaturedWeight);
                    Sort.applyCreatorBasedFairSortingWeights(sortedPackages);
                    sortedPackages.sort(Sort.compareByFeaturedWeight);
                    return Sort.distributePackages(sortedPackages);
                },
                comparator: Sort.compareByFeaturedWeight,
            },
            relevance: {
                name: t("Relevance"),
                // This is hidden, kept as a backup so we can easily switch back to it if needed
                // TODO: Remove Relevance sort once we're fully confident in the Featured sort
                visible: false,
                comparator: Sort.compareByRelevance,
            },
            name: {
                name: t("Name: A-Z"),
                icon: "fa-sort-alpha-down",
                reverse: { name: t("Name: Z-A"), icon: "fa-sort-alpha-up-alt" },
                comparator: Sort.compareByTitle,
            },
            price: {
                name: t("Price: Low to high"),
                icon: "fa-sort-numeric-down",
                reverse: {
                    name: t("Price: High to low"),
                    icon: "fa-sort-numeric-up-alt",
                },
                comparator: Sort.compareByPrice,
            },
            newest: {
                name: t("Newest releases"),
                comparator: Sort.compareByNewest,
            },
            latest: {
                name: t("Recently updated"),
                comparator: Sort.compareByLatest,
            },
            installed: {
                name: t("Installs"),
                comparator: Sort.compareByInstalls,
            },
            ratings: {
                name: t("Ratings: High to low"),
                reverse: { name: t("Ratings: Low to high") },
                visible: false, // This is hidden until the ratings are implemented
                comparator: Sort.compareByRatings,
            },
            favorites: {
                name: t("Favorites First"),
                visible: false, // This is hidden until the favorites are implemented
                comparator: Sort.compareByFavorite,
            },
        };
    }

    /**
     * Compares two packages based on their search weight.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} - A negative number if `b` has a higher weight, a positive number if `a` has a higher weight, or 0 if they have equal weight.
     */
    static compareByWeight(a, b) {
        const aWeight = (a.search && a.search.weight) || 0;
        const bWeight = (b.search && b.search.weight) || 0;
        return bWeight - aWeight;
    }

    /**
     * Compares two packages based on their featured weight.
     * If the featured weights are equal, it falls back to comparing by installs.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} - A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are equal.
     */
    static compareByFeaturedWeight(a, b) {
        const aWeight = (a.search && a.search.featuredWeight) || 0;
        const bWeight = (b.search && b.search.featuredWeight) || 0;
        if (aWeight === bWeight) {
            return Sort.compareByInstalls(a, b, true);
        }
        return bWeight - aWeight;
    }

    /**
     * Compares two items by their relevance.
     * First, it compares the items by their weight using `Sort.compareByWeight`.
     * If the weights are equal, it then compares the items by their installs using `Sort.compareByInstalls`.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} - A negative number if `a` is more relevant than `b`, a positive number if `a` is less relevant than `b`, or 0 if they are equally relevant.
     */
    static compareByRelevance(a, b) {
        const weightComparison = Sort.compareByWeight(a, b);
        if (weightComparison === 0) {
            return Sort.compareByInstalls(a, b, true);
        }
        return weightComparison;
    }

    /**
     * Compares two packages by their normalized titles.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @param {boolean} dir - The direction of the comparison. If true, sorts in ascending order; otherwise, sorts in descending order.
     * @returns {number} - A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are considered equal.
     */
    static compareByTitle(a, b, dir) {
        const aTitle = Sort.normalizeTitle(a.title);
        const bTitle = Sort.normalizeTitle(b.title);
        if (dir) {
            return aTitle.localeCompare(bTitle);
        }
        return bTitle.localeCompare(aTitle);
    }

    /**
     * Compares two packages by price, with optional sorting direction.
     * Falls back to comparing by title if prices are equal.
     * @param {object} a - The first package.
     * @param {object} b - The second package.
     * @param {boolean} dir - Sort direction; true for ascending, false for descending.
     * @returns {number} - Sorting order indicator.
     */
    static compareByPrice(a, b, dir) {
        const aPrice = Number(a.pwywMinimum === undefined ? a.price : a.pwywMinimum) || 0;
        const bPrice = Number(b.pwywMinimum === undefined ? b.price : b.pwywMinimum) || 0;
        if (aPrice === bPrice) {
            return Sort.compareByTitle(a, b, true);
        }
        if (dir) {
            return aPrice - bPrice;
        }
        return bPrice - aPrice;
    }

    /**
     * Compares two packages based on their publication or creation dates in descending order.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} - A negative number if `b` is newer than `a`, a positive number if `a` is newer than `b`, or zero if they are equal.
     */
    static compareByNewest(a, b) {
        const aDate = a.firstPublishAt || a.created || 0;
        const bDate = b.firstPublishAt || b.created || 0;
        return bDate - aDate;
    }

    /**
     * Compares two packages based on their latest update, first publish, or creation date.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} A negative number if `a` is more recent than `b`, a positive number if `b` is more recent than `a`, or zero if they are equal.
     */
    static compareByLatest(a, b) {
        const aDate = a.updated || a.firstPublishAt || a.created || 0;
        const bDate = b.updated || b.firstPublishAt || b.created || 0;
        return bDate - aDate;
    }

    /**
     * Compares two packages based on the number of installs, with an additional weighting factor
     * based on how recently the objects were updated. If the number of installs is the same,
     * it falls back to comparing by title.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @param {boolean} dir - The direction of the sort. If true, sorts in descending order.
     * @returns {number} - A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are considered equal.
     */
    static compareByInstalls(a, b, dir) {
        const aInstalls = a.installs || 0;
        const bInstalls = b.installs || 0;
        if (aInstalls === bInstalls) {
            return Sort.compareByTitle(a, b, dir);
        }
        // Weight the number of installs based on how long ago it was updated
        // Anything older than a year is only worth half as much
        const aWeight = aInstalls * Sort.calculateInstallsWeight(a.updated || a.firstPublishAt || a.created || 0);
        const bWeight = bInstalls * Sort.calculateInstallsWeight(b.updated || b.firstPublishAt || b.created || 0);
        if (dir) {
            return bWeight - aWeight;
        }
        return aWeight - bWeight;
    }

    /**
     * Compares two packages based on their ratings.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @param {boolean} dir - The direction of the sort. If true, sorts in descending order; otherwise, sorts in ascending order.
     * @returns {number} - A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are equal.
     */
    static compareByRatings(a, b, dir) {
        const aRatings = a.ratings || 0;
        const bRatings = b.ratings || 0;
        if (aRatings === bRatings) {
            return Sort.compareByTitle(a, b, dir);
        }
        if (dir) {
            return bRatings - aRatings;
        }
        return aRatings - bRatings;
    }

    /**
     * Compares two packages based on their "favorites" property.
     * If both objects have the same "favorites" status, it falls back to comparing by title.
     *
     * @param {Object} a - The first package to compare.
     * @param {Object} b - The second package to compare.
     * @returns {number} - A negative number if `a` should come before `b`, a positive number if `a` should come after `b`, or 0 if they are equal.
     */
    static compareByFavorite(a, b) {
        const aFavorites = a.favorites ? 1 : 0;
        const bFavorites = b.favorites ? 1 : 0;
        if (aFavorites === bFavorites) {
            return Sort.compareByTitle(a, b, true);
        }
        return bFavorites - aFavorites;
    }

    /**
     * Normalizes a given title by converting it to lowercase and removing a leading "the ".
     *
     * @param {string} [title=""] - The title to normalize.
     * @returns {string} The normalized title.
     */
    static normalizeTitle(title = "") {
        return title.toLowerCase().replace(/^the /, "");
    }

    /**
     * Calculates the weight of installs based on the last update time.
     *
     * @param {number} updated - The timestamp of the last update in milliseconds.
     * @returns {number} The calculated weight of the installs.
     */
    static calculateInstallsWeight(updated) {
        return updated < YEAR_AGO_MS ? 0.5 : 0.5 + 0.5 * ((updated - YEAR_AGO_MS) / YEAR_MS);
    }

    /**
     * Sorts an array of packages based on a specified key and direction.
     *
     * @param {Array} packages - The array of packages to be sorted.
     * @param {Object} [options] - The sorting options.
     * @param {string} [options.key=DEFAULT_SORT] - The key to sort by.
     * @param {boolean|string} [options.dir=true] - The direction to sort by. If "desc", sorts in descending order.
     * @returns {Array} The sorted array of packages.
     */
    static sort(packages, { key = DEFAULT_SORT, dir = true } = {}) {
        const sortOption = Sort.sortTags[key];
        if (!sortOption) {
            return packages;
        }
        const sortDirection = dir !== "desc";
        if (sortOption.sort) {
            return sortOption.sort(packages, sortDirection);
        }
        return packages.sort((a, b) => sortOption.comparator(a, b, sortDirection));
    }

    /**
     * @typedef {Object} PackageSearchData
     * @property {boolean} visible - whether the package is visible.
     * @property {number} matches - number of times a search term was matched in this package,
     *   e.g. when searching for text in the description.
     * @property {number} weight - the weight of the package based on the search terms. Higher
     *   means it matches the search terms better.
     * @property {boolean} marketplace - whether the package is a marketplace package.
     * @property {number} daysSincePublished - number of days since the package was published, always
     *   positive.
     * @property {number} installs - installs percentage multiplied by 100, always positive.
     */

    /**
     * Calculate the weight of a featured package, based on an assumption that new
     * packages should have certain amount of priority days without making the visibility
     * of the popular packages worse.
     *
     * **Note**: the weight for non-marketplace packages is based solely on the installs percentage,
     * while marketplace packages use all of the terms described below to incorporate time since it
     * was published.
     *
     * The math is based on the fact we want a quick fall-off for the new packages,
     * while making older, popular packages still have enough weight to be visible among them.
     * For the purposes of this calculation, "installs" is 100 times the installs percentage of
     * the package. For example, a package installed on 1.01% of Forge games will have 101 for its
     * installs value.
     *
     * FEATURED_SORT_DAY_FALLOFF / (daysSincePublished + 1) - this part of the formula ensures that
     * new packages have a higher weighting than popular old packages for several days.
     *
     * ln(ln(installs + 1) + 1) - this part of the formula adds weight based on the popularity of
     * the package. The weight is logarithmic, so past about 20 (that is, 0.2% of Forge games) the
     * weight gain is minimal. This value is then multiplied by FEATURED_SORT_INSTALLS_WEIGHT and
     * clipped to FEATURED_SORT_INSTALLS_WEIGHT_CEIL to prevent dramatically popular packages (dnd5e)
     * from overshadowing everything else.
     *
     * Initially we set FEATURED_SORT_DAY_FALLOFF to 45, FEATURED_SORT_INSTALLS_WEIGHT to 2, and
     * FEATURED_SORT_INSTALLS_WEIGHT_CEIL to 3.5. This means that new packages with no installs will
     * outweigh all old packages for the first 11 days. Past that, the package will drop in
     * visibility unless it has gained some traction. These constants can be adjusted to modify the
     * behaviour of the algorithm.
     * @param {PackageSearchData} pkgSearchData Search data for this package. The name or other
     *   metadata do not matter, this object contains everything that is needed to calculate the
     *   featured sort weight.
     * @returns {number} The weight of the package for the "Featured" sorting algorithm. Should
     *   typically fall in the range of (0, 5], with values higher than that essentially
     *   guaranteeing top placement.
     */
    static calculateFeaturedWeight(pkgSearchData) {
        const daysWeight = FEATURED_SORT_DAY_FALLOFF / (pkgSearchData.daysSincePublished + 1);
        const installsLog = Math.log(Math.log(pkgSearchData.installs + 1) + 1);
        const installsWeight = Math.min(
            installsLog * FEATURED_SORT_INSTALLS_WEIGHT,
            FEATURED_SORT_INSTALLS_WEIGHT_CEIL
        );
        const searchWeight = pkgSearchData.weight + 1;
        return pkgSearchData.marketplace ? (daysWeight + installsWeight) * searchWeight : installsWeight * searchWeight;
    }

    /**
     * This method recalculates the weights of the packages, based on author
     * making package featuredWeight fall off for subsequent packages from the
     * same creator/author.
     *
     * The ensures that someone who posts multiple new packages in the span of several
     * days will not have them displayed one by one, but interspersed with other packages.
     * Likewise for creators with a lot of high-popularity packages (Forgotten Adventures).
     * @param {PackageJSON[]} sortedPackages An array of first-pass-sorted packages.
     * @returns {PackageJSON[]} A reference to sortedPackages, after featured weights have been
     *   redistributed to demote subsequent packages from the same creator/author.
     */
    static applyCreatorBasedFairSortingWeights(sortedPackages) {
        const creatorWeights = {};
        for (const pkg of sortedPackages) {
            const creatorId = pkg.creator?.vanity || pkg.authors?.[0] || pkg.author;
            if (creatorId) {
                if (creatorWeights[creatorId]) {
                    creatorWeights[creatorId] *= 0.5;
                } else {
                    creatorWeights[creatorId] = 1;
                }
                pkg.search.featuredWeight *= creatorWeights[creatorId];
            }
        }
        return sortedPackages;
    }

    /**
     * This method distributes already sorted packages based on the rules defined in the method,
     * while keeping relative order of the packages when possible. This makes sure that the packages
     * have a fair chance of being seen, while still keeping the most relevant ones at the top.
     *
     * The method has two parts:
     * 1. Initial distribution - this method moves certain packages to front or back of the sorted list,
     *    and then spreads the rest of the packages based on different fields with proper weights.
     *    Distribution rules are evaluated in order of appearance. This is done by {@link distributePackagesByField}
     * 2. Rule based distribution - this method applies rules to the packages, based on the different defined rules,
     *    making sure the distances between different packages are kept. Rules are executed in order of appearance.
     *
     * Currently implemented rules:
     * 1. `type.system` - spreads the system packages, so they appear not more often than 4-5 packages apart
     * 2. `premium.marketplace` - spreads the marketplace packages, so they appear not more often than 3 packages apart
     * 3. `installed` - moves the installed packages to the end of the list
     * 4. `updated` - moves the packages that can be updated packages to the front of the list
     *
     * @param sortedPackages pre-sorted packages to be distributed
     * @returns {*[]} distributed packages
     */
    static distributePackages(sortedPackages) {
        if (!sortedPackages) {
            return [];
        }
        const spreadFields = {
            updatable: {
                evaluate: (pkg) => {
                    const target = pkg.versionToInstall || pkg.latest;
                    // Store original value of package.installed to avoid having the package's
                    // sort position change if it gets updated/uninstalled by the user
                    if (!Object.prototype.hasOwnProperty.call(pkg, "_originallyInstalled")) {
                        pkg._originallyInstalled = pkg.installed;
                    }
                    return (
                        pkg._originallyInstalled &&
                        pkg.versions.length > 0 &&
                        target &&
                        pkg._originallyInstalled !== target
                    );
                },
                front: true,
            },
            installed: {
                evaluate: (pkg) => {
                    // Store original value of package.installed to avoid having the package's
                    // sort position change if it gets installed/uninstalled by the user
                    if (!Object.prototype.hasOwnProperty.call(pkg, "_originallyInstalled")) {
                        pkg._originallyInstalled = pkg.installed;
                    }
                    return pkg._originallyInstalled;
                },
                end: true,
            },
            premium: { field: "premium", values: ["marketplace"], spread: 2 },
            type: { field: "type", values: ["system"], spread: 3 },
        };
        const distributionRules = [
            { ruleName: "type.system", applies: (pkg) => pkg.type === "system", distance: 5, distanceVariation: 1 },
            { ruleName: "premium.marketplace", applies: (pkg) => pkg.premium === "marketplace", distance: 3 },
        ];

        // These two arrays are passed to distributePackagesByField as accumulators, which populates them accordingly.
        const frontPackages = [];
        const endPackages = [];
        // spread the packages while keeping them sorted. all elements in the spreadFields should be sorted separately
        // based on their values, with rest being lumped together
        for (const field of Object.keys(spreadFields)) {
            sortedPackages = Sort.distributePackagesByField(
                sortedPackages,
                frontPackages,
                endPackages,
                spreadFields[field]
            );
        }

        // This mapping keeps track of the last index which was used for a rule,
        // so we can enforce the distance specified in the rule. It is a map of
        // ruleName -> lastPosition
        const lastPackagePositionForRule = new Map();

        for (let i = 0; i < sortedPackages.length; i++) {
            if (!sortedPackages[i]) {
                break;
            }
            const pkg = sortedPackages[i];
            let moveDistance = 0;
            const appliedRules = [];
            for (const rule of distributionRules) {
                if (!rule.stop && rule.applies(pkg)) {
                    // If there are no more packages which DO NOT fit the rule, stop distributing to avoid
                    // an infinite loop.
                    if (!sortedPackages.slice(i + 1).some((p) => !rule.applies(p))) {
                        rule.stop = true;
                    }
                    if (rule.distance) {
                        if (!lastPackagePositionForRule.has(rule.ruleName)) {
                            lastPackagePositionForRule.set(rule.ruleName, Number.MIN_SAFE_INTEGER);
                        }
                        if (
                            i - lastPackagePositionForRule.get(rule.ruleName) <
                            rule.distance - rule.distanceVariation
                        ) {
                            moveDistance = Math.max(rule.distance, moveDistance);
                            if (i + moveDistance >= sortedPackages.length) {
                                moveDistance = sortedPackages.length - i - 1;
                            }
                        }
                    }
                    appliedRules.push(rule);
                }
            }
            pkg.appliedRules = appliedRules;
            if (moveDistance > 0) {
                sortedPackages.splice(i, 1);
                sortedPackages.splice(i + moveDistance, 0, pkg);
                i--;
            }
            for (const rule of appliedRules) {
                lastPackagePositionForRule.set(rule.ruleName, i + moveDistance);
            }
        }
        // join front + sorted + end
        return frontPackages.concat(sortedPackages).concat(endPackages);
    }

    /**
     * This method distributes the packages based on the field and the spreadField rules or evaluation method.
     *
     * The method has two modes of operation:
     * 1. If the `spreadField` has `spreadField.front` or `spreadField.end` set, then it will distribute the packages to front/end of the array
     *    if the evaluation method returns true for the package
     * 2. Else, it will distribute the packages based on the field values, with the `spreadField.spread` number of packages per value
     *
     * @param {PackageJSON[]} sortedPackages Sorted packages to be distributed
     * @param {PackageJSON[]} frontPackages An accumulator for packages to be moved to the front
     * @param {PackageJSON[]} endPackages An accumulator for packages to be moved to the end
     * @param {Object} spreadField Definition of the spread logic for this field.
     * @param {string} [spreadField.field] Package field name to evaluate
     * @param {string[]} [spreadField.values] Values to match in the field
     * @param {Function} [spreadField.evaluate] Customized evaluation logic
     * @param {boolean} [spreadField.front] Move into frontPackages
     * @param {boolean} [spreadField.end] Move into endPackages
     * @param {number} [spreadField.spread] Minimum number of non-matching packages between each matching package
     * @returns {PackageJSON[]} distributed packages
     */
    static distributePackagesByField(sortedPackages, frontPackages, endPackages, spreadField) {
        const REMAINING_GROUP = "remaining";
        if (!spreadField.end && !spreadField.front) {
            const spreadGroups = new Map();
            for (const pkg of sortedPackages) {
                const group = spreadField.evaluate
                    ? spreadField.evaluate(pkg)
                    : spreadField.values.includes(pkg[spreadField.field])
                      ? pkg[spreadField.field]
                      : REMAINING_GROUP;
                if (!spreadGroups.has(group)) {
                    spreadGroups.set(group, []);
                }
                spreadGroups.get(group).push(pkg);
            }
            const newSortedPackages = [];
            while (spreadGroups.size > 0) {
                for (const group of spreadGroups.keys()) {
                    for (let i = 0; i < (group === REMAINING_GROUP ? spreadField.spread : 1); i++) {
                        if (!spreadGroups.get(group)) {
                            break;
                        }
                        const pkg = spreadGroups.get(group).shift();
                        if (!pkg) {
                            spreadGroups.delete(group);
                        } else {
                            newSortedPackages.push(pkg);
                        }
                    }
                }
            }
            return newSortedPackages;
        }
        const distributedPackages = [];
        for (const pkg of sortedPackages) {
            if (spreadField.evaluate(pkg)) {
                if (spreadField.end) {
                    endPackages.push(pkg);
                } else {
                    frontPackages.push(pkg);
                }
            } else {
                distributedPackages.push(pkg);
            }
        }
        return distributedPackages;
    }
}
